import { useEffect, useState } from "react";
import {
  Typography,
  Table,
  Input,
  Button,
  Select,
  Switch,
  Space,
  message,
} from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { normalizeControllerName } from "@/utils/usbControllerNames";
import {
  subscribeToUsbControllers,
  subscribeToUsbDevices,
  subscribeToUsbCompatConfig,
  updateControllerNotes,
  updateControllerCompatible,
  setCatalogComplete,
  type UsbController,
  type UsbDevice,
} from "@/services/usbService";

const { Title, Text } = Typography;

export default function UsbCatalogPage() {
  const [controllers, setControllers] = useState<UsbController[]>([]);
  const [devices, setDevices] = useState<UsbDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [catalogComplete, setCatalogCompleteState] = useState(false);

  // Controller notes editing
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    const unsub1 = subscribeToUsbControllers((items) => {
      setControllers(items);
      setLoading(false);
    });
    const unsub2 = subscribeToUsbDevices(setDevices);
    const unsub3 = subscribeToUsbCompatConfig((cfg) => setCatalogCompleteState(cfg.catalogComplete));
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  // --- Controllers (grouped by normalized name) ---
  interface GroupedController {
    normalizedName: string;
    ids: string[];
    rawNames: string[];
    notes: string;
    // Resolved verdict for the group: true (good) wins, then false (bad), else undefined.
    compatible?: boolean;
  }

  const groupedControllers: GroupedController[] = (() => {
    const groups = new Map<string, GroupedController>();
    for (const c of controllers) {
      const norm = normalizeControllerName(c.name);
      const existing = groups.get(norm);
      if (existing) {
        existing.ids.push(c.id);
        existing.rawNames.push(c.name);
        if (c.notes && !existing.notes) existing.notes = c.notes;
        if (existing.compatible === undefined && c.compatible !== undefined) {
          existing.compatible = c.compatible;
        } else if (c.compatible === true) {
          existing.compatible = true;
        }
      } else {
        groups.set(norm, {
          normalizedName: norm,
          ids: [c.id],
          rawNames: [c.name],
          notes: c.notes || "",
          compatible: c.compatible,
        });
      }
    }
    return Array.from(groups.values()).sort((a, b) =>
      a.normalizedName.localeCompare(b.normalizedName)
    );
  })();

  const handleSetCompatible = async (group: GroupedController, value: boolean | null) => {
    try {
      await Promise.all(group.ids.map((id) => updateControllerCompatible(id, value)));
      message.success("Compatibility updated");
    } catch {
      message.error("Failed to update compatibility");
    }
  };

  const handleSaveNotes = async (group: GroupedController) => {
    try {
      const value = editingNotes[group.normalizedName] ?? "";
      await Promise.all(group.ids.map((id) => updateControllerNotes(id, value)));
      setEditingNotes((prev) => { const next = { ...prev }; delete next[group.normalizedName]; return next; });
      message.success("Notes saved");
    } catch {
      message.error("Failed to save notes");
    }
  };

  const controllerColumns = [
    { title: "Name", dataIndex: "normalizedName", key: "name", width: 360 },
    {
      title: "Notes",
      key: "notes",
      render: (_: unknown, record: GroupedController) => {
        const isEditing = record.normalizedName in editingNotes;
        if (isEditing) {
          return (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                value={editingNotes[record.normalizedName]}
                onChange={(e) => setEditingNotes((prev) => ({ ...prev, [record.normalizedName]: e.target.value }))}
                onPressEnter={() => handleSaveNotes(record)}
              />
              <Button type="primary" onClick={() => handleSaveNotes(record)}>Save</Button>
              <Button onClick={() => setEditingNotes((prev) => { const next = { ...prev }; delete next[record.normalizedName]; return next; })}>
                Cancel
              </Button>
            </Space.Compact>
          );
        }
        return (
          <Space>
            <Text>{record.notes || "—"}</Text>
            <Button
              type="text"
              size="small"
              icon={<MaterialIcon name="edit" size={14} />}
              onClick={() => setEditingNotes((prev) => ({ ...prev, [record.normalizedName]: record.notes || "" }))}
            />
          </Space>
        );
      },
    },
    {
      title: "Compatibility",
      key: "compatible",
      width: 220,
      render: (_: unknown, record: GroupedController) => {
        const value =
          record.compatible === true ? "good" :
          record.compatible === false ? "bad" : "unrated";
        return (
          <Select
            size="small"
            style={{ width: 200 }}
            value={value}
            onChange={(v) => handleSetCompatible(record, v === "good" ? true : v === "bad" ? false : null)}
            options={[
              { value: "good", label: "✅ Compatible" },
              { value: "bad", label: "❌ Not compatible" },
              { value: "unrated", label: "— Unrated" },
            ]}
          />
        );
      },
    },
    {
      title: "Devices",
      key: "deviceCount",
      width: 80,
      render: (_: unknown, record: GroupedController) =>
        devices.filter((d) => record.rawNames.includes(d.controller)).length,
    },
  ];

  return (
    <div>
      <Title level={4}>USB Controllers</Title>

      <Table
        dataSource={groupedControllers}
        columns={controllerColumns}
        rowKey="normalizedName"
        size="small"
        pagination={false}
        loading={loading}
        style={{ marginTop: 16 }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
        <Switch
          checked={catalogComplete}
          onChange={async (checked) => {
            try {
              await setCatalogComplete(checked);
              message.success(checked ? "Catalog marked complete" : "Catalog marked incomplete");
            } catch {
              message.error("Failed to update catalog flag");
            }
          }}
        />
        <Text type="secondary">
          Block unknown USB controllers
        </Text>
      </div>

    </div>
  );
}
